/**
 * DEV-2521: URL preset parsing for `/booking/create`.
 *
 * Presets only skip selections. They never bypass authorization, validation, review, or
 * explicit submission. Identity, guest data, and notes are never carried in URLs.
 */
import {
  BookingKind,
  MaintenanceType,
  bookingTypeChoiceFor,
  isBookingKind,
  isCreatableMaintenanceType
} from './bookingKinds';

/**
 * `type` takes the Type step choices: `owner`, `guest`, `maintenance`, or `owner-access`.
 * `maintenanceType` is only read from older links.
 */
export const BOOKING_PRESET_PARAMS = {
  listingId: 'listing',
  reservationType: 'type',
  maintenanceType: 'maintenanceType'
} as const;

export type PresetName = 'listing' | 'kind' | 'maintenanceType';

export type PresetResult<T> =
  | { status: 'absent' }
  | { status: 'valid'; value: T }
  | { status: 'invalid'; message: string };

export interface BookingPresets {
  listing: PresetResult<string>;
  kind: PresetResult<BookingKind>;
  maintenanceType: PresetResult<MaintenanceType>;
  /** True when the listing preset came from the legacy `/booking/create/:id` path. */
  listingFromLegacyPath: boolean;
}

export const PRESET_MESSAGES = {
  listingConflict:
    'This link points to two different properties. Please confirm the property you want to book.',
  listingMalformed: 'This link does not include a valid property. Please select a property.',
  listingUnavailable:
    'The property in this link is not available for booking. Please select a property.',
  kindMalformed:
    'This link does not include a valid reservation type. Please confirm the type of booking.',
  maintenanceTypeMalformed:
    'This link does not include a valid maintenance type. Please confirm the type of block.'
} as const;

export const ABSENT: PresetResult<never> = { status: 'absent' };

/**
 * Read exactly one value for a known parameter. Empty or repeated values are treated as
 * malformed so the corresponding step is shown with a message instead of silently guessing.
 */
function readSingle(params: URLSearchParams, key: string): { present: boolean; value: string | null } {
  const values = params.getAll(key);
  if (values.length === 0) {
    return { present: false, value: null };
  }
  if (values.length > 1) {
    return { present: true, value: null };
  }
  const [only] = values;
  if (only.trim() === '' || only !== only.trim()) {
    return { present: true, value: null };
  }
  return { present: true, value: only };
}

/**
 * Parse URL presets at the create-page boundary.
 *
 * The listing value is only a candidate here; the form validates it against the loaded
 * authorized/eligible listings and never substitutes a different property.
 *
 * @param search the location search string (with or without the leading `?`)
 * @param legacyPathListingId the `:id` from the legacy `/booking/create/:id` route, if any
 */
export function parseBookingPresets(
  search: string,
  legacyPathListingId?: string | null
): BookingPresets {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  // --- listing ---------------------------------------------------------------------------
  const queryListing = readSingle(params, BOOKING_PRESET_PARAMS.listingId);
  const legacyId =
    legacyPathListingId && legacyPathListingId.trim() !== '' ? legacyPathListingId.trim() : null;

  let listing: PresetResult<string> = ABSENT;
  let listingFromLegacyPath = false;

  if (legacyId && queryListing.present) {
    listingFromLegacyPath = true;
    if (queryListing.value === null) {
      listing = { status: 'invalid', message: PRESET_MESSAGES.listingMalformed };
    } else if (queryListing.value !== legacyId) {
      listing = { status: 'invalid', message: PRESET_MESSAGES.listingConflict };
    } else {
      listing = { status: 'valid', value: legacyId };
    }
  } else if (legacyId) {
    listingFromLegacyPath = true;
    listing = { status: 'valid', value: legacyId };
  } else if (queryListing.present) {
    listing =
      queryListing.value !== null
        ? { status: 'valid', value: queryListing.value }
        : { status: 'invalid', message: PRESET_MESSAGES.listingMalformed };
  }

  // --- kind ------------------------------------------------------------------------------
  const queryKind = readSingle(params, BOOKING_PRESET_PARAMS.reservationType);
  let kind: PresetResult<BookingKind> = ABSENT;
  const isOwnerAccess = queryKind.value === 'owner-access';
  if (queryKind.present) {
    if (isOwnerAccess) {
      kind = { status: 'valid', value: 'maintenance' };
    } else {
      kind = isBookingKind(queryKind.value)
        ? { status: 'valid', value: queryKind.value }
        : { status: 'invalid', message: PRESET_MESSAGES.kindMalformed };
    }
  }

  // --- maintenance subtype (only for a maintenance kind) ---------------------------------
  const queryMaintenance = readSingle(params, BOOKING_PRESET_PARAMS.maintenanceType);
  let maintenanceType: PresetResult<MaintenanceType> = ABSENT;
  if (kind.status === 'valid' && kind.value === 'maintenance') {
    if (isOwnerAccess) {
      maintenanceType = { status: 'valid', value: 'owner-access' };
    } else if (!queryMaintenance.present) {
      maintenanceType = { status: 'valid', value: 'maintenance' };
    } else if (queryMaintenance.value === 'vendor' || queryMaintenance.value === 'housekeeping') {
      // Vendor and Housekeeping are no longer creatable block types; map them to Maintenance.
      maintenanceType = { status: 'valid', value: 'maintenance' };
    } else {
      maintenanceType = isCreatableMaintenanceType(queryMaintenance.value)
        ? { status: 'valid', value: queryMaintenance.value }
        : { status: 'invalid', message: PRESET_MESSAGES.maintenanceTypeMalformed };
    }
  }

  return { listing, kind, maintenanceType, listingFromLegacyPath };
}

export interface BookingPresetQuery {
  listingId?: string | null;
  reservationType?: BookingKind | null;
  maintenanceType?: MaintenanceType | null;
}

/** Build the canonical `/booking/create` search string from validated preset values. */
export function buildBookingPresetSearch(query: BookingPresetQuery): string {
  const params = new URLSearchParams();
  if (query.listingId) {
    params.set(BOOKING_PRESET_PARAMS.listingId, query.listingId);
  }
  if (query.reservationType) {
    params.set(
      BOOKING_PRESET_PARAMS.reservationType,
      bookingTypeChoiceFor(query.reservationType, query.maintenanceType ?? null)
    );
  }
  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** Convert parsed presets back into the query values that are still valid. */
export function presetsToQuery(presets: BookingPresets): BookingPresetQuery {
  return {
    listingId: presets.listing.status === 'valid' ? presets.listing.value : null,
    reservationType: presets.kind.status === 'valid' ? presets.kind.value : null,
    maintenanceType:
      presets.maintenanceType.status === 'valid' ? presets.maintenanceType.value : null
  };
}

/**
 * Remove one preset (used by the Change links). Invalid presets for that name are removed as
 * well so the message disappears once the owner has confirmed the selection. Changing away
 * from the kind also removes the maintenance subtype preset. Other presets are preserved.
 */
export function removePreset(presets: BookingPresets, name: PresetName): BookingPresetQuery {
  const query = presetsToQuery(presets);
  switch (name) {
    case 'listing':
      return { ...query, listingId: null };
    case 'kind':
      return { ...query, reservationType: null, maintenanceType: null };
    case 'maintenanceType':
      return { ...query, maintenanceType: null };
    default:
      return query;
  }
}
